import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAuth } from './auth/AuthContext';
import { useIsAdmin } from './auth/useIsAdmin';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { NotAuthorizedPage } from './pages/NotAuthorizedPage';
import { AdminHubProvider, useAdminHub } from './contexts/AdminHubContext';
import { DashboardPage } from './pages/DashboardPage';
import { LiveEventTailPage } from './pages/LiveEventTailPage';
import { JobHistoryPage } from './pages/JobHistoryPage';
import { QueueHealthPage } from './pages/QueueHealthPage';
import { CostAnalysisPage } from './pages/CostAnalysisPage';
import { HealthCheckAuditPage } from './pages/HealthCheckAuditPage';
import { ModerationQueuePage } from './pages/ModerationQueuePage';
import { PostModerationQueuePage } from './pages/moderation/PostModerationQueuePage';
import { ProducersListPage } from './pages/feed/ProducersListPage';
import { ProducerDetailPage } from './pages/feed/ProducerDetailPage';
import { KnowledgeQueuePage } from './pages/KnowledgeQueuePage';
import { UserRosterPage } from './pages/UserRosterPage';
import { ReviewOutcomesPage } from './pages/ReviewOutcomesPage';
import { HumanReviewQueuePage } from './pages/HumanReviewQueuePage';
import { SharedRecordReviewPage } from './pages/SharedRecordReviewPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AiConfigPage } from './pages/AiConfigPage';
import { NotificationTemplatesPage } from './pages/NotificationTemplatesPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { TaskTemplatesPage } from './pages/TaskTemplatesPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { BusinessAgentsPage } from './pages/agents/BusinessAgentsPage';
import { ProvidersPage } from './pages/ProvidersPage';
import { ExpertsPage } from './pages/ExpertsPage';
import { setTokenProvider } from './api/authenticatedFetch';
import { isDevModeAllowed } from './utils/devModeEnabled';

// Reports pages (spec 016) — lazy-loaded; later waves add more routes here.
const OnboardingFunnelPage = lazy(() => import('./pages/reports/OnboardingFunnelPage'));
const ActivationFunnelPage = lazy(() => import('./pages/reports/ActivationFunnelPage'));
const UserDetailPage = lazy(() => import('./pages/reports/UserDetailPage'));
const AiGenerationPage = lazy(() => import('./pages/reports/AiGenerationPage'));
const BoardsReportPage = lazy(() => import('./pages/reports/BoardsReportPage'));
const EngagementPage = lazy(() => import('./pages/reports/EngagementPage'));
const BillingReportPage = lazy(() => import('./pages/reports/BillingReportPage'));
const UsageReportPage = lazy(() => import('./pages/reports/UsageReportPage'));
const UserSearchPage = lazy(() => import('./pages/reports/UserSearchPage'));

function ReportsFallback() {
  return (
    <main className="flex-1 flex items-center justify-center">
      <span className="text-zinc-500 text-sm">Loading report…</span>
    </main>
  );
}

// ---------------------------------------------------------------------------
// DevMode detection — mirrors user app pattern: ?devmode=true
// Persisted to sessionStorage so it survives client-side navigation.
//
// Build-time gate: the MSAL bypass only exists in builds where devmode is
// allowed (see utils/devModeEnabled.ts) — prod builds strip it entirely.
// ---------------------------------------------------------------------------
function useDevMode(): boolean {
  if (!isDevModeAllowed()) return false;
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('devmode') === 'true';
  if (fromUrl) {
    try { sessionStorage.setItem('admin.devmode', 'true'); } catch { /* ignore */ }
  }
  try {
    return fromUrl || sessionStorage.getItem('admin.devmode') === 'true';
  } catch {
    return fromUrl;
  }
}

// ---------------------------------------------------------------------------
// Inner header reads connection status from the hub context
// ---------------------------------------------------------------------------
function AdminHeader({
  isDevMode,
  navOpen,
  onToggleNav,
}: {
  isDevMode: boolean;
  navOpen: boolean;
  onToggleNav: () => void;
}) {
  const { status } = useAdminHub();
  const { logout } = useAuth();
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const connStatus =
    status === 'Connected'
      ? 'Connected'
      : status === 'Connecting'
      ? 'Connecting'
      : 'Disconnected';
  const onSignOut = isDevMode
    ? undefined
    : () => {
        setSignOutError(null);
        logout().catch(() => setSignOutError('Sign out failed. Please try again.'));
      };
  return (
    <Header
      isDevMode={isDevMode}
      connectionStatus={connStatus}
      onSignOut={onSignOut}
      signOutError={signOutError}
      onDismissSignOutError={() => setSignOutError(null)}
      navOpen={navOpen}
      onToggleNav={onToggleNav}
    />
  );
}

// ---------------------------------------------------------------------------
// Admin Shell — layout + routes, shown to authenticated admins
// ---------------------------------------------------------------------------
function AdminShell({ isDevMode }: { isDevMode: boolean }) {
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  return (
    <AdminHubProvider>
      <div className="flex min-h-screen bg-zinc-950 text-zinc-100">
        <Sidebar open={navOpen} onNavigate={() => setNavOpen(false)} />
        <div className="flex flex-col flex-1 min-w-0">
          <AdminHeader isDevMode={isDevMode} navOpen={navOpen} onToggleNav={() => setNavOpen((o) => !o)} />
          <RouteErrorBoundary resetKey={pathname}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/live" element={<LiveEventTailPage />} />
            <Route path="/jobs" element={<JobHistoryPage />} />
            <Route path="/queues" element={<QueueHealthPage />} />
            <Route path="/cost" element={<CostAnalysisPage />} />
            <Route path="/health-checks" element={<HealthCheckAuditPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/moderation" element={<ModerationQueuePage />} />
            <Route path="/moderation/posts" element={<PostModerationQueuePage />} />
            <Route path="/feed/producers" element={<ProducersListPage />} />
            <Route path="/feed/producers/:name" element={<ProducerDetailPage />} />
            <Route path="/agents" element={<BusinessAgentsPage />} />
            <Route path="/providers" element={<ProvidersPage />} />
            <Route path="/experts" element={<ExpertsPage />} />
            <Route path="/knowledge" element={<KnowledgeQueuePage />} />
            <Route path="/users" element={<UserRosterPage />} />
            <Route path="/review-outcomes" element={<ReviewOutcomesPage />} />
            <Route path="/review-queue" element={<HumanReviewQueuePage />} />
            <Route path="/shared-records" element={<SharedRecordReviewPage />} />
            <Route path="/config" element={<AiConfigPage />} />
            <Route path="/templates" element={<NotificationTemplatesPage />} />
            <Route path="/task-templates" element={<TaskTemplatesPage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route
              path="/reports/onboarding"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <OnboardingFunnelPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/activation"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <ActivationFunnelPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/ai-generation"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <AiGenerationPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/boards"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <BoardsReportPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/engagement"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <EngagementPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/billing"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <BillingReportPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/usage"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <UsageReportPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/users"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <UserSearchPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/users/:profileId"
              element={
                <Suspense fallback={<ReportsFallback />}>
                  <UserDetailPage />
                </Suspense>
              }
            />
            <Route path="*" element={<Navigate to="/live" replace />} />
          </Routes>
          </RouteErrorBoundary>
        </div>
      </div>
    </AdminHubProvider>
  );
}

// ---------------------------------------------------------------------------
// Loading page — shared by the MSAL-interaction wait and the role-resolution
// wait, so a cold-load probe never flashes a different screen than the
// familiar "Loading…" state.
// ---------------------------------------------------------------------------
function LoadingPage() {
  return (
    <main className="flex-1 flex items-center justify-center min-h-screen bg-zinc-950">
      <span className="text-zinc-500 text-sm">Loading…</span>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Sign-in page — shown to unauthenticated users
// ---------------------------------------------------------------------------
function SignInPage() {
  const { login, isLoading } = useAuth();

  return (
    <main className="flex-1 flex flex-col items-center justify-center min-h-screen bg-zinc-950">
      <div className="text-center max-w-md px-6">
        <h1 className="text-2xl font-bold text-zinc-100 mb-2">Chelless Admin</h1>
        <p className="text-zinc-400 text-sm mb-8">
          Sign in with your admin Azure B2C account to access the monitoring dashboard.
        </p>
        <button
          type="button"
          onClick={() => void login()}
          disabled={isLoading}
          className="px-6 py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-zinc-950 font-medium rounded-lg transition-colors min-h-[48px]"
        >
          {isLoading ? 'Signing in…' : 'Sign in with Azure B2C'}
        </button>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// App root — auth gate
// ---------------------------------------------------------------------------
function AppInner() {
  const isDevMode = useDevMode();
  const isAuthenticated = useIsAuthenticated();
  const { isLoading, isRoleResolved, getAccessToken } = useAuth();
  const isAdmin = useIsAdmin(isDevMode);

  // Wire the authenticated fetch client to the MSAL token source so that
  // all API calls (orchestratorClient + adminModerationService) automatically
  // attach the Bearer token. Cleared on unmount to avoid stale references.
  useEffect(() => {
    setTokenProvider(getAccessToken);
    return () => { setTokenProvider(null); };
  }, [getAccessToken]);

  if (isDevMode) {
    return <AdminShell isDevMode={true} />;
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <SignInPage />;
  }

  if (!isRoleResolved) {
    // Cold-load probe in flight (see AuthContext's resolvedIsAdmin effect) —
    // stay on Loading rather than flash "Not Authorized" for an admin whose
    // snapshot came back without role claims.
    return <LoadingPage />;
  }

  if (!isAdmin) {
    return <NotAuthorizedPage />;
  }

  return <AdminShell isDevMode={false} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
